import { createError, getErrorCauseChain } from '@adv-frontend/error';
import { isObject } from '@direct-frontend/stdlib';

import type { Flow } from '../contract/flow';
import { isFlowLike } from '../utils/isFlowLike';

/**
 * Ключ, под которым на объекте ошибки живут поток и его снапшот на момент броска.
 *
 * Symbol, а не строковое поле: такой ключ не мешает сериализации — SSR-дамп и
 * `JSON.stringify` его просто не видят, а потоку в дамп ехать и не надо.
 * Наружу из пакета ключ не экспортируется, доступ только через {@link getErrorFlows}.
 */
const ERROR_FLOW = Symbol('@adv-frontend/modules-flow/errorFlow');

/**
 * Ошибка-обертка над ошибкой из состояния AsyncFlow.
 *
 * Исходная ошибка лежит в `cause`, а поток и его снапшот на момент броска — под приватным
 * symbol-ключом (см. {@link getErrorFlows}). Такую ошибку бросают accessor'ы `useAsyncFlow`, когда
 * поток находится в состоянии `error`.
 *
 * Сообщение статичное: все содержимое несет `cause`, а одинаковый текст хорошо группируется
 * в Error Booster.
 */
export const AsyncFlowError = createError('AsyncFlowError');

/** Поток, привязанный к ошибке, вместе с его состоянием на момент броска */
export interface ErrorFlowRef {
    /** Поток, из состояния которого взята ошибка */
    flow: Flow<unknown>;
    /**
     * Состояние потока на момент создания ошибки.
     *
     * По нему потребитель понимает, двигался ли поток с тех пор: пока состояние не менялось,
     * снапшот остается тем же объектом.
     */
    snapshotAtThrow: unknown;
}

/**
 * Собирает потоки вместе со снапшотами, с которых была брошена ошибка, — по самой ошибке
 * и по всем звеньям ее цепочки `cause`.
 *
 * @param err - Ошибка, пойманная Error Boundary, или любая другая
 * @returns Потоки со снапшотами в порядке от внешней ошибки к внутренней, без повторов по потоку
 *
 * @remarks
 * Обычно поток ровно один: `useAsyncFlow` создает одну обертку на бросок, а ошибка
 * вышестоящего потока идет через вычисляемые потоки без обертки. Несколько бывает, только
 * если в цепочке `cause` уже лежит несколько `AsyncFlowError`.
 *
 * После SSR-гидрации потока в ошибке нет: сериализация symbol-ключи не переносит. Потери нет —
 * обертку создает `useAsyncFlow` в момент броска, то есть на клиенте поток записывается
 * заново.
 *
 * @example
 * ```ts
 * const moved = getErrorFlows(error).some(ref => ref.flow.getSnapshot() !== ref.snapshotAtThrow);
 * ```
 */
export function getErrorFlows(err: unknown): ErrorFlowRef[] {
    const refs: ErrorFlowRef[] = [];

    for (const link of getErrorCauseChain(err)) {
        const ref = readErrorFlow(link);

        if (ref && !refs.some(known => known.flow === ref.flow)) {
            refs.push(ref);
        }
    }

    return refs;
}

/**
 * Создает ошибку-обертку для броска из accessor'а `useAsyncFlow`.
 *
 * @param error - Ошибка из состояния потока
 * @param flow - Поток, из состояния которого взята ошибка
 * @param snapshotAtThrow - Состояние потока, из которого взята ошибка
 * @returns Обертка, в которой лежат поток и его снапшот на момент броска
 *
 * @remarks
 * Обертка создается на каждый бросок и служит носителем потока, а исходная ошибка остается
 * нетронутой. Носитель обязан быть свой у каждого потребителя: ошибка батчевого запроса общая
 * на весь чанк, и запись потока в нее заставила бы потребителей перетирать потоки друг друга.
 */
export function createAsyncFlowError(
    error: unknown,
    flow: Flow<unknown>,
    snapshotAtThrow: unknown
): InstanceType<typeof AsyncFlowError> {
    const wrapped = new AsyncFlowError('Async flow is in the error state', { cause: error });
    const ref: ErrorFlowRef = { flow, snapshotAtThrow };

    Object.defineProperty(wrapped, ERROR_FLOW, {
        value: ref,
        writable: true,
        configurable: true,
        enumerable: false,
    });

    return wrapped;
}

function readErrorFlow(error: object): ErrorFlowRef | undefined {
    if (!(ERROR_FLOW in error)) {
        return undefined;
    }

    const ref = error[ERROR_FLOW];

    // Значение под ключом может оказаться чем угодно: ошибку мог собрать чужой код или
    // приехать частично восстановленной после гидрации
    if (!isObject(ref) || !('flow' in ref) || !isFlowLike(ref.flow)) {
        return undefined;
    }

    return { flow: ref.flow, snapshotAtThrow: 'snapshotAtThrow' in ref ? ref.snapshotAtThrow : undefined };
}
